import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Send } from 'lucide-react';
import { CONTACT, SERVICES } from '../data/siteData';
import { INQUIRY_SERVICES, initialInquiry, inquiryServiceForSlug, sendInquiry } from '../lib/inquiries';

export default function InquiryForm() {
  const { search } = useLocation();
  const requestedSlug = new URLSearchParams(search).get('service') || '';
  const requestedService = SERVICES.find((item) => item.slug === requestedSlug) || null;
  const requestedChoice = requestedService ? inquiryServiceForSlug(requestedService.slug) : null;
  const [form, setForm] = useState(() => ({ ...initialInquiry, service: requestedChoice || initialInquiry.service }));
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(null);
  const inFlight = useRef(false);
  const requestId = useRef(null);
  const message = useRef(null);
  const field = (name) => ({ name, value: form[name], onChange: (event) => setForm((current) => ({ ...current, [name]: event.target.value })) });
  const exactServiceRequest = requestedService && form.kind === 'service' && form.service === requestedChoice;

  useEffect(() => {
    if (!requestedChoice) return;
    setForm((current) => current.service === requestedChoice ? current : { ...current, service: requestedChoice });
  }, [requestedChoice]);

  async function submit(event) {
    event.preventDefault();
    if (inFlight.current || saved) return;
    inFlight.current = true;
    setStatus('sending'); setError('');
    try {
      // Keep this key after errors/timeouts: retrying must not create another inquiry.
      requestId.current ||= crypto.randomUUID();
      // Keep the exact service (e.g. flatbed towing) alongside the validated
      // TowOps service category. Preserve the customer's own explanation.
      const serviceContext = exactServiceRequest ? `Website service page: ${requestedService.title}\n` : '';
      const payload = serviceContext
        ? { ...form, details: (serviceContext + form.details).slice(0, 3000) }
        : form;
      const result = await sendInquiry(payload, requestId.current);
      setSaved(result); setStatus('success');
      // Count only newly accepted inquiries as leads. Never transmit customer details.
      if (!result.duplicate) {
        try {
          window.rokaAnalytics?.track?.('lead_submit', {
            ctaId: 'website_inquiry',
            ctaLabel: form.kind === 'service' ? `Service inquiry: ${exactServiceRequest ? requestedService.title : form.service}` : `Inquiry: ${form.kind}`,
          });
        } catch {
          // Analytics must never interfere with a confirmed TowOps inquiry.
        }
      }
    } catch (failure) {
      setError(failure.message || 'Delivery could not be confirmed. Please call or text dispatch.');
      setStatus('error');
    } finally {
      inFlight.current = false;
      setTimeout(() => message.current?.focus(), 0);
    }
  }

  function startAgain() {
    requestId.current = null; setSaved(null); setForm({ ...initialInquiry, service: requestedChoice || initialInquiry.service }); setStatus('idle'); setError('');
  }

  return <form className="contact-form" id="service-request" onSubmit={submit} aria-busy={status === 'sending'}>
    <div className="form-heading"><span className="eyebrow">Contact dispatch</span><h2>Send a request or inquiry</h2></div>
    <p className="inquiry-notice">For urgent roadside help, <a href={CONTACT.phoneHref} data-roka-cta="call_dispatch" data-roka-label="Call dispatch">call {CONTACT.phoneDisplay}</a>. This form requests a response; it does not confirm service, a price, or an arrival time.</p>
    {exactServiceRequest && <p className="inquiry-notice">Requested service: <strong>{requestedService.title}</strong>. Dispatch will confirm the equipment, availability and pricing.</p>}
    <fieldset className="inquiry-fields" disabled={status === 'sending' || Boolean(saved)}>
      <div className="form-grid">
        <label className="form-full"><span>What is this about?</span><select {...field('kind')}><option value="service">Towing or roadside assistance</option><option value="holiday">Holiday trucks or community event</option><option value="general">General inquiry</option></select></label>
        <label><span>Name</span><input {...field('name')} autoComplete="name" required minLength={2} maxLength={160} /></label>
        <label><span>Phone</span><input {...field('phone')} type="tel" autoComplete="tel" required minLength={7} maxLength={40} /></label>
        <label className="form-full"><span>Email (optional)</span><input {...field('email')} type="email" autoComplete="email" maxLength={200} /></label>
        {form.kind === 'service' && <>
          <label><span>Current location</span><input {...field('location')} required minLength={3} maxLength={500} placeholder="Street address, city, or nearest intersection" /></label>
          <label><span>Destination (when applicable)</span><input {...field('destination')} maxLength={500} /></label>
          <label><span>Vehicle (when known)</span><input {...field('vehicle')} maxLength={200} placeholder="Year, make and model" /></label>
          <label><span>Service requested</span><select {...field('service')}>{INQUIRY_SERVICES.map((service) => <option key={service}>{service}</option>)}</select></label>
        </>}
        <label className="form-full"><span>{form.kind === 'service' ? 'What happened?' : 'How can we help?'}</span><textarea {...field('details')} required minLength={5} maxLength={3000} rows="5" /></label>
        <label className="inquiry-trap" aria-hidden="true"><span>Leave this field empty</span><input {...field('website')} tabIndex={-1} autoComplete="off" /></label>
      </div>
      <p>Your details are used to respond to this inquiry. <Link to="/privacy-policy">Privacy policy</Link>.</p>
      <button className="button button--primary" type="submit"><Send size={18} /> {status === 'sending' ? 'Saving your request…' : 'Send request'}</button>
    </fieldset>
    <div ref={message} tabIndex={-1} aria-live="polite" aria-atomic="true">
      {saved && <div className="form-message success inquiry-reference" role="status"><strong>Request received — {saved.reference}</strong><p>{form.kind === 'service' ? 'Dispatch will contact you to confirm availability, details, and pricing. Your service is not confirmed until you hear from us.' : 'Your inquiry is saved with Jonny’s Towing. We will contact you to discuss the details.'}</p>{saved.duplicate && <p>Your earlier request was found; another request was not created.</p>}<button type="button" className="button button--secondary" onClick={startAgain}>Start a separate inquiry</button></div>}
      {error && <div className="form-message error" role="alert"><p>{error}</p><p>Your entered details have not been cleared. <a href={CONTACT.phoneHref} data-roka-cta="call_dispatch" data-roka-label="Call dispatch">Call dispatch</a> or <a href={CONTACT.textHref} data-roka-cta="text_dispatch" data-roka-label="Text dispatch">text dispatch</a>.</p></div>}
    </div>
    <noscript><p>To submit this form, enable JavaScript. You can also <a href={CONTACT.phoneHref} data-roka-cta="call_dispatch" data-roka-label="Call dispatch">call dispatch</a> or <a href={CONTACT.textHref} data-roka-cta="text_dispatch" data-roka-label="Text dispatch">text dispatch</a>.</p></noscript>
  </form>;
}
